import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Loader2,
    ChevronLeft,
    ChevronRight,
    MessageCircle,
    Mail,
    Smartphone,
    Phone,
    UserRound,
    Send,
} from "lucide-react";
import { callComConfigApi } from "./callComConfigApi";
import AuthContext  from "../../../store/auth-context";
import ActiveStoreContext from "../../../store/ActiveStoreContext";
import styles from "./ComConfigResolutionPreview.module.css";

// recipientTypeId is a numeric FK with no store-facing list endpoint to
// resolve codes -> IDs (same gap noted in ComConfigMyTemplates), so this
// screen expects it passed in as a prop by whoever opens the preview
// (e.g. ComConfigEventPolicy, which already has it from context) rather
// than letting the user pick it here.

const CHANNEL_ICONS = {
    WHATSAPP: MessageCircle,
    SMS: Smartphone,
    EMAIL: Mail,
    VOICE: Phone,
};

const ComConfigResolutionPreview = () => {
    const { eventId, recipientTypeId } = useParams();
    const languageCode = "en";
    const navigate = useNavigate();
    const authCtx = useContext(AuthContext);
    const { activeStore: activeStoreJson } = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return activeStoreJson ? JSON.parse(activeStoreJson) : null; } catch { return null; } })();

    const [plan, setPlan] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        runPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId, recipientTypeId, languageCode, activeStore?.storeIdentifier]);

    const runPreview = async () => {
        if (!activeStore?.storeIdentifier || !eventId || !recipientTypeId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        const res = await callComConfigApi(
            "comConfigResolvePlan",
            {
                storeIdentifier: activeStore.storeIdentifier,
                eventId,
                recipientTypeId,
                languageCode,
            },
            authCtx
        );

        if (res?.status === "success") {
            setPlan(res);
        } else {
            setError(res?.message || "Couldn't resolve a plan for this combination.");
        }

        setIsLoading(false);
    };

    if (isLoading) {
        return (
            <div className={styles.stateContainer}>
                <Loader2 className={styles.spinner} size={22} />
                <span>Working out what would be sent…</span>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <button className={styles.backLink} onClick={() => navigate(`/communication-settings/${eventId}`)}>
                <ChevronLeft size={16} />
                Back
            </button>

            {error && <div className={styles.messageError}>{error}</div>}

            {plan && (
                <>
                    <div className={styles.header}>
                        <h1 className={styles.title}>{plan.eventName}</h1>
                        <span className={plan.strategySource === "STORE_OVERRIDE" ? styles.sourceCustom : styles.sourceDefault}>
                            {plan.strategySource === "STORE_OVERRIDE" ? "Your custom strategy" : "Platform default"}
                        </span>
                    </div>

                    <div className={styles.stepList}>
                        {plan.steps?.map((step, index) => {
                            const ChannelIcon = CHANNEL_ICONS[step.channelCode] || MessageCircle;
                            return (
                                <div key={index} className={styles.stepCard}>
                                    <div className={styles.stepCardHeader}>
                                        <div className={styles.stepChannelTag}>
                                            <ChannelIcon size={15} />
                                            {step.channelName}
                                        </div>
                                        <span className={styles.stepOrder}>Step {step.stepOrder}</span>
                                    </div>

                                    {step.resolvedTemplate ? (
                                        <p className={styles.stepContent}>{step.resolvedTemplate.content}</p>
                                    ) : (
                                        <p className={styles.stepContentMissing}>
                                            No content configured for this channel yet.
                                        </p>
                                    )}

                                    <div className={styles.stepFooter}>
                                        <div className={styles.senderInfo}>
                                            <Send size={12} />
                                            {step.resolvedSender
                                                ? step.resolvedSender.storeIdentifier === "PLATFORM"
                                                    ? "Sent via Lokaci"
                                                    : "Sent via your own account"
                                                : "No sender configured"}
                                        </div>
                                        {step.conditionJson && (
                                            <div className={styles.conditionInfo}>
                                                <UserRound size={12} />
                                                Only if: {Object.keys(step.conditionJson).join(", ")}
                                            </div>
                                        )}
                                    </div>

                                    {index < plan.steps.length - 1 && (
                                        <ChevronRight size={16} className={styles.stepConnector} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

export default ComConfigResolutionPreview;